import React, { useMemo } from "react";
import Chart from "./Chart";
import KPI from "./KPI";
import Table from "./Table";
import OrdersHeatmap from "./Heatmap";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec",];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const CARD_CLASS =
  "bg-white border border-gray-200 rounded-lg p-4 shadow-sm dark:bg-gray-900 dark:border-gray-700";
const TITLE_CLASS =
  "text-sm text-blue-700 dark:text-blue-300 font-medium mb-2";
const EMPTY_CLASS = "text-sm text-gray-500 dark:text-gray-400";

const formatMonthDay = (isoDate) => {
  if (!isoDate) return "";

  const [, month, day] = String(isoDate)
    .slice(0, 10)
    .split("-")
    .map(Number);

  if (
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    month < 1 ||
    month > 12
  ) {
    return String(isoDate);
  }

  return `${MONTHS[month - 1]} ${day}`;
};

const formatHour12 = (hour) => {
  const h = Number(hour);

  if (!Number.isFinite(h) || h < 0 || h > 23) {
    return "";
  }

  const suffix = h < 12 ? "AM" : "PM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;

  return `${hour12}:00 ${suffix}`;
};

const toWeekStartYmd = (isoDate) => {
  if (!isoDate) return null;

  const date = new Date(`${String(isoDate).slice(0, 10)}T00:00:00Z`);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const weekdayMon0 = (date.getUTCDay() + 6) % 7;

  date.setUTCDate(date.getUTCDate() - weekdayMon0);

  return date.toISOString().slice(0, 10);
};

const weekdayFromIso = (isoDate) => {
  if (!isoDate) return null;

  const date = new Date(`${String(isoDate).slice(0, 10)}T00:00:00Z`);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const weekdayMon0 = (date.getUTCDay() + 6) % 7;

  return WEEKDAYS[weekdayMon0];
};

const ChartsSection = ({
  ordersByDay = [],
  avgBumpTimeByDay = [],
  ordersHeatmap = [],
  itemsData = [],
  ordersList = [],
  ordersBumpMinutes = [],
  isDark = true,
}) => {
  // Combine daily order totals with daily average bump times for the overview chart.
  const overlayData = useMemo(() => {
    const map = new Map();

    for (const row of ordersByDay) {
      const date = String(row?.date || "").slice(0, 10);

      if (!date) continue;

      map.set(date, {
        ...(map.get(date) || {}),
        date,
        total_orders: Number(row?.total_orders || 0),
      });
    }

    for (const row of avgBumpTimeByDay) {
      const date = String(row?.date || "").slice(0, 10);

      if (!date) continue;

      map.set(date, {
        ...(map.get(date) || {}),
        date,
        avg_bump_time_minutes: Number(
          row?.avg_bump_time_minutes || 0,
        ),
      });
    }

    return Array.from(map.values()).sort((a, b) =>
      String(a.date).localeCompare(String(b.date)),
    );
  }, [ordersByDay, avgBumpTimeByDay]);

  /* Build weekly totals grouped by weekday for the day-of-week chart. */
  const dayOfWeekTrendData = useMemo(() => {
    const weekMap = new Map();

    for (const row of ordersByDay) {
      const date = String(row?.date || "").slice(0, 10);
      const weekStart = toWeekStartYmd(date);
      const weekday = weekdayFromIso(date);

      if (!weekStart || !weekday) continue;

      if (!weekMap.has(weekStart)) {
        weekMap.set(weekStart, {
          week_start: weekStart,
          Mon: 0,
          Tue: 0,
          Wed: 0,
          Thu: 0,
          Fri: 0,
          Sat: 0,
          Sun: 0,
        });
      }

      weekMap.get(weekStart)[weekday] += Number(
        row?.total_orders || 0
      );
    }

    return Array.from(weekMap.values()).sort((a, b) =>
      String(a.week_start).localeCompare(String(b.week_start))
    );
  }, [ordersByDay]);

  /* Build one Overview Report row per day using all available ticket data. */
  const overviewRows = useMemo(() => {
    const dailyMap = new Map();

    const ensureDay = (date) => {
      const day = String(date || "").slice(0, 10);

      if (!day) return null;

      if (!dailyMap.has(day)) {
        dailyMap.set(day, {
          id: day,
          date: day,
          orders: 0,
          avgBump: null,
          bumpValues: [],
          ticketsCompleted: 0,
          hourlyOrders: new Map(),
        });
      }

      return dailyMap.get(day);
    };

    /* Add the backend daily ticket totals. */
    for (const row of ordersByDay) {
      const day = ensureDay(row?.date);

      if (!day) continue;

      day.orders = Number(row?.total_orders || 0);
    }

    /* Add the backend daily average bump time. */
    for (const row of avgBumpTimeByDay) {
      const day = ensureDay(row?.date);

      if (!day) continue;

      const avg = Number(row?.avg_bump_time_minutes);

      if (Number.isFinite(avg)) {
        day.avgBump = avg;
      }
    }

    /* Create a lookup of order ID to bump duration. */
    const bumpMap = new Map();

    for (const row of ordersBumpMinutes) {
      const orderId = row?.order_id;
      const bumpMinutes = Number(row?.bump_minutes);

      if (
        orderId == null ||
        !Number.isFinite(bumpMinutes) ||
        bumpMinutes < 0
      ) {
        continue;
      }

      bumpMap.set(String(orderId), bumpMinutes);
    }

    /* Process individual tickets so daily median and performance can be calculated. */
    for (const order of ordersList) {
      const orderId = String(order?.order_id ?? "");

      const receivedAt =
        order?.received_at ||
        order?.created_at ||
        order?.date;

      const orderDate = String(receivedAt || "").slice(0, 10);

      if (!orderDate) continue;

      const day = ensureDay(orderDate);

      if (!day) continue;

      /* Count tickets that have been completed/bumped. */
      if (order?.bumped_at) {
        day.ticketsCompleted += 1;
      }

      /* Count orders by received hour for the daily peak hour. */
      if (receivedAt) {
        const receivedDate = new Date(receivedAt);

        if (!Number.isNaN(receivedDate.getTime())) {
          const hour = receivedDate.getHours();

          day.hourlyOrders.set(
            hour,
            (day.hourlyOrders.get(hour) || 0) + 1
          );
        }
      }

      /* Get bump time from the backend data. */
      let bumpMinutes = bumpMap.get(orderId);

      /* Calculate bump time directly when the backend lookup has no match. */
      if (
        !Number.isFinite(bumpMinutes) &&
        order?.bumped_at &&
        receivedAt
      ) {
        const receivedDate = new Date(receivedAt);
        const bumpedDate = new Date(order.bumped_at);

        if (
          !Number.isNaN(receivedDate.getTime()) &&
          !Number.isNaN(bumpedDate.getTime())
        ) {
          const calculatedMinutes =
            (bumpedDate.getTime() - receivedDate.getTime()) / 60000;

          if (
            Number.isFinite(calculatedMinutes) &&
            calculatedMinutes >= 0
          ) {
            bumpMinutes = calculatedMinutes;
          }
        }
      }

      if (
        Number.isFinite(bumpMinutes) &&
        bumpMinutes >= 0
      ) {
        day.bumpValues.push(bumpMinutes);
      }
    }

    return Array.from(dailyMap.values())
      .sort((a, b) =>
        String(a.date).localeCompare(String(b.date))
      )
      .map((day) => {
        /* Sort ticket bump times for median calculation. */
        const bumpValues = [...day.bumpValues]
          .filter(
            (value) =>
              Number.isFinite(value) &&
              value >= 0
          )
          .sort((a, b) => a - b);

        /* Calculate the median bump time for the day. */
        let median = null;

        if (bumpValues.length) {
          const middle = Math.floor(bumpValues.length / 2);

          median =
            bumpValues.length % 2 === 1
              ? bumpValues[middle]
              : (bumpValues[middle - 1] +
                  bumpValues[middle]) /
                2;
        }

        /* Use the backend's daily average bump time. */
        const avg = Number.isFinite(day.avgBump)
          ? day.avgBump
          : null;

        /* Find the busiest order hour for the day. */
        let peakHour = "-";
        let peakHourOrders = 0;

        for (const [hour, count] of day.hourlyOrders) {
          if (count > peakHourOrders) {
            peakHourOrders = count;
            peakHour = hour;
          }
        }

        if (Number.isFinite(peakHour)) {
          peakHour = formatHour12(peakHour);
        }

        /* Classify ticket bump times against the day's average and median. */
        let normalCount = 0;
        let needsReviewCount = 0;
        let outlierCount = 0;

        if (
          Number.isFinite(avg) &&
          Number.isFinite(median) &&
          bumpValues.length
        ) {
          const lower = Math.min(avg, median);
          const upper = Math.max(avg, median);
          const reviewBuffer = 250;

          for (const minutes of bumpValues) {
            if (
              minutes >= lower &&
              minutes <= upper
            ) {
              normalCount += 1;
            } else if (
              minutes >= lower - reviewBuffer &&
              minutes <= upper + reviewBuffer
            ) {
              needsReviewCount += 1;
            } else {
              outlierCount += 1;
            }
          }
        }

        /* Convert the daily ticket classifications into one performance status. */
        let timePerformance = "-";

        const classifiedTickets =
          normalCount +
          needsReviewCount +
          outlierCount;

        if (classifiedTickets > 0) {
          const normalPercent =
            (normalCount / classifiedTickets) * 100;

          if (normalPercent >= 80) {
            timePerformance = "Normal";
          } else if (normalPercent >= 50) {
            timePerformance = "Needs Review";
          } else {
            timePerformance = "Outlier";
          }
        }

        return {
          id: day.date,
          date: day.date,
          orders: day.orders,
          avg_bump: avg,
          median_bump: median,
          slow_tickets: outlierCount,
          peak_hour: peakHour,
          time_performance: timePerformance,
          tickets_completed: day.ticketsCompleted,
        };
      });
  }, [
    ordersByDay,
    avgBumpTimeByDay,
    ordersList,
    ordersBumpMinutes,
  ]);

  /* Define the Overview Report columns. */
  const overviewColumns = [
    {
      field: "date",
      headerName: "Date",
      width: 120,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "orders",
      headerName: "Tickets",
      width: 100,
      type: "number",
      align: "left",
      headerAlign: "left",
    },
    {
      field: "avg_bump",
      headerName: "Avg Bump",
      width: 120,
      type: "number",
      align: "left",
      headerAlign: "left",
      valueFormatter: (value) =>
        value == null
          ? "-"
          : `${Number(value).toFixed(2)} min`,
    },
    {
      field: "median_bump",
      headerName: "Median Bump",
      width: 130,
      type: "number",
      align: "left",
      headerAlign: "left",
      valueFormatter: (value) =>
        value == null
          ? "-"
          : `${Number(value).toFixed(2)} min`,
    },
    {
      field: "slow_tickets",
      headerName: "Slow Tickets",
      width: 120,
      type: "number",
      align: "left",
      headerAlign: "left",
    },
    {
      field: "peak_hour",
      headerName: "Peak Hour",
      width: 120,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "time_performance",
      headerName: "Time Performance",
      flex: 1,
      minWidth: 150,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        const status = params.value;

        const styles = {
          Normal:
            "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
          "Needs Review":
            "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
          Outlier:
            "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
        };

        if (!status || status === "-") {
          return (
            <span className="text-gray-400">
              -
            </span>
          );
        }

        return (
          <span
            className={`
              px-2
              py-1
              rounded
              text-xs
              font-medium
              ${styles[status] || ""}
            `}
          >
            {status}
          </span>
        );
      },
    },
    {
      field: "tickets_completed",
      headerName: "Tickets Completed",
      width: 150,
      type: "number",
      align: "left",
      headerAlign: "left",
    },
  ];

  /* Calculate the overall total number of tickets. */
  const totalOrders = useMemo(
    () =>
      ordersByDay.reduce(
        (sum, row) =>
          sum + Number(row?.total_orders || 0),
        0
      ),
    [ordersByDay]
  );

  /* Calculate the overall average from individual order bump durations. */
  const averageBumpTime = useMemo(() => {
    const values = ordersBumpMinutes
      .map((row) =>
        Number(row?.bump_minutes)
      )
      .filter((value) => Number.isFinite(value) && value >= 0);

    if (!values.length) {
      return "—";
    }

    const average =
      values.reduce(
        (sum, value) => sum + value,
        0
      ) / values.length;

    return `${average.toFixed(1)} min`;
  }, [ordersBumpMinutes]);

  const averageItemsPerTicket = useMemo(() => {
    if (totalOrders <= 0) return "—";

    const backendTotal = Number(itemsData[0]?.total_items_sold);
    const totalItems = Number.isFinite(backendTotal)
      ? backendTotal
      : itemsData.reduce(
          (sum, row) => sum + Number(row?.total_quantity || 0),
          0
        );

    return (totalItems / totalOrders).toFixed(1);
  }, [itemsData, totalOrders]);

  /* Determine the overall peak order period. */
  const peakOrderPeriod = useMemo(() => {
    if (!ordersHeatmap.length) {
      return "—";
    }

    const peak = ordersHeatmap.reduce(
      (best, row) =>
        Number(row?.total_orders || 0) >
        Number(best?.total_orders || 0)
          ? row
          : best,
      ordersHeatmap[0]
    );

    return peak
      ? formatHour12(peak.hour_of_day)
      : "—";
  }, [ordersHeatmap]);

  return (
    <div className="grid grid-cols-1 gap-3 mt-6">
      {/* KPI ROW */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <KPI
          title="Total Orders"
          value={totalOrders.toLocaleString()}
          color="blue"
        />

        <KPI
          title="Avg. Ticket Bump Time"
          value={averageBumpTime}
          color="emerald"
        />

        <KPI
          title="Peak Order Period"
          value={peakOrderPeriod}
          color="yellow"
        />

        <KPI
          title="Avg. Items Per Ticket"
          value={averageItemsPerTicket}
          color="purple"
        />
      </div>

      {/* ORDERS & TICKET PERFORMANCE */}
      <Chart
        type="line"
        title="Orders & Ticket Performance"
        subtitle="Daily order volume and average ticket bump time"
        data={overlayData}
        xAxis={[
          {
            dataKey: "date",
            scaleType: "band",
            valueFormatter: (value) =>
              formatMonthDay(value),
            tickLabelStyle: {
              fontSize: 11,
            },
          },
        ]}
        yAxis={[
          {
            id: "orders",
            label: "Orders",
            min: 0,
          },
          {
            id: "bumpTime",
            label: "Minutes",
            min: 0,
          },
        ]}
        series={[
          {
            dataKey: "total_orders",
            label: "Orders",
            yAxisId: "orders",
          },
          {
            dataKey: "avg_bump_time_minutes",
            label: "Avg. Bump Time (min)",
            yAxisId: "bumpTime",
          },
        ]}
        xAxisTitle="Date"
        isDark={isDark}
        height={400}
      />

      {/* PEAK HOUR HEATMAP */}
      <div className={CARD_CLASS}>
        <h4 className={TITLE_CLASS}>
          Peak Hour Heatmap (Orders)
        </h4>

        {ordersHeatmap.length > 0 ? (
          <OrdersHeatmap
            data={ordersHeatmap}
            isDark={isDark}
          />
        ) : (
          <p className={EMPTY_CLASS}>
            No data available for peak-hour heatmap.
          </p>
        )}
      </div>

      {/* DAY OF WEEK AND TOP ITEMS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Chart
          type="bar"
          title="Total Orders by Day of Week"
          data={dayOfWeekTrendData}
          isDark={isDark}
          height={320}
          xAxis={[
            {
              dataKey: "week_start",
              scaleType: "band",
              label: "Week",
              valueFormatter: (value) =>
                formatMonthDay(value),
              tickLabelStyle: {
                fontSize: 11,
              },
            },
          ]}
          yAxis={[
            {
              label: "Orders",
              min: 0,
              tickLabelStyle: {
                fontSize: 11,
              },
            },
          ]}
          series={[
            {
              dataKey: "Mon",
              label: "Monday",
            },
            {
              dataKey: "Tue",
              label: "Tuesday",
            },
            {
              dataKey: "Wed",
              label: "Wednesday",
            },
            {
              dataKey: "Thu",
              label: "Thursday",
            },
            {
              dataKey: "Fri",
              label: "Friday",
            },
            {
              dataKey: "Sat",
              label: "Saturday",
            },
            {
              dataKey: "Sun",
              label: "Sunday",
            },
          ]}
        />

        <Chart
          type="bar"
          title="Top Items"
          data={itemsData}
          xAxis={[
            {
              dataKey: "name",
              scaleType: "band",
            },
          ]}
          yAxis={[
            {
              label: "Orders",
              min: 0,
            },
          ]}
          series={[
            {
              dataKey: "total_quantity",
              label: "Times Ordered",
            },
          ]}
          xAxisTitle="Item"
          yAxisTitle="Orders"
          isDark={isDark}
          height={350}
        />
      </div>

      {/* OVERVIEW REPORT */}
      <Table
        title="Overview Report"
        rows={overviewRows}
        columns={overviewColumns}
        isDark={isDark}
        getRowId={(row) => row.id}
        searchable
        searchPlaceholder="Search by date..."
        exportable
        exportTitle="Overview Report"
        exportFileName="Overview Report"
      />
    </div>
  );
};

export default ChartsSection;
